import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Save, ArrowLeft, Eye, AlertCircle, CheckCircle2 } from "lucide-react";
import { fetchPostById, createPost, updatePost } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/auth";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const CATEGORIES = ["news", "formazione", "normativa", "aed", "aziende", "casi-studio"];

const EMPTY = {
    title: "",
    slug: "",
    summary: "",
    content: "# Titolo\n\nScrivi qui il tuo contenuto in **markdown**.\n\n- Lista\n- Con punti\n\n> Citazione importante\n",
    author: "7MED",
    category: "news",
    cover_image: "",
    status: "draft",
    tags: "",
};

export default function AdminBlogEditor() {
    const { id } = useParams();
    const isEdit = !!id;
    const nav = useNavigate();
    const [form, setForm] = useState(EMPTY);
    const [state, setState] = useState({ loading: isEdit, saving: false, error: null, success: null });

    useEffect(() => {
        if (!isEdit) return;
        fetchPostById(id)
            .then((p) => setForm({
                title: p.title, slug: p.slug, summary: p.summary, content: p.content,
                author: p.author || "7MED", category: p.category || "news",
                cover_image: p.cover_image || "", status: p.status || "draft",
                tags: (p.tags || []).join(", "),
            }))
            .catch((e) => setState({ loading: false, saving: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message, success: null }))
            .finally(() => setState((s) => ({ ...s, loading: false })));
    }, [id, isEdit]);

    const setField = (k, v) => setForm((s) => ({ ...s, [k]: v }));

    async function onSave(e, overrideStatus) {
        e?.preventDefault();
        setState((s) => ({ ...s, saving: true, error: null, success: null }));
        try {
            const payload = {
                title: form.title.trim(),
                slug: form.slug.trim() || undefined,
                summary: form.summary.trim(),
                content: form.content,
                author: form.author.trim() || "7MED",
                category: form.category,
                cover_image: form.cover_image.trim() || null,
                status: overrideStatus || form.status,
                tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
            };
            const res = isEdit ? await updatePost(id, payload) : await createPost(payload);
            setState({ loading: false, saving: false, error: null, success: isEdit ? "Articolo aggiornato." : "Articolo creato." });
            if (!isEdit) nav(`/admin/blog/${res.id}`, { replace: true });
            if (overrideStatus) setForm((s) => ({ ...s, status: overrideStatus }));
        } catch (err) {
            setState({ loading: false, saving: false, error: formatApiErrorDetail(err.response?.data?.detail) || err.message, success: null });
        }
    }

    if (state.loading) return <div className="text-center py-24 text-ink-muted">Caricamento…</div>;

    return (
        <form onSubmit={onSave} className="space-y-8" data-testid="admin-editor">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <Link to="/admin/blog" className="text-xs font-mono uppercase tracking-[0.2em] text-ink-muted hover:text-brand inline-flex items-center gap-2">
                        <ArrowLeft className="w-3.5 h-3.5" /> Tutti gli articoli
                    </Link>
                    <h1 className="mt-3 font-heading font-black text-4xl tracking-tighter">{isEdit ? "Modifica articolo" : "Nuovo articolo"}</h1>
                </div>
                <div className="flex items-center gap-3">
                    <button type="button" onClick={(e) => onSave(e, "draft")} disabled={state.saving} className="btn-secondary" data-testid="admin-save-draft">
                        {state.saving ? "Salvo..." : "Salva bozza"}
                    </button>
                    <button type="button" onClick={(e) => onSave(e, "published")} disabled={state.saving} className="btn-primary" data-testid="admin-publish">
                        <Save className="w-4 h-4" /> {form.status === "published" ? "Aggiorna pubblicato" : "Pubblica"}
                    </button>
                </div>
            </div>

            {state.error && (
                <div className="flex gap-3 items-start p-4 bg-red-50 border border-red-300 text-red-900">
                    <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">{state.error}</div>
                </div>
            )}
            {state.success && (
                <div className="flex gap-3 items-start p-4 bg-emerald-50 border border-emerald-300 text-emerald-900" data-testid="admin-save-success">
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">{state.success}</div>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                <div className="lg:col-span-8 space-y-6 bg-white border border-ink/10 p-6 lg:p-8">
                    <Field label="Titolo" required>
                        <input required value={form.title} onChange={(e) => setField("title", e.target.value)} className="editor-input" placeholder="Il titolo dell'articolo" data-testid="editor-title" />
                    </Field>
                    <Field label="Slug URL (opzionale — generato automaticamente)">
                        <input value={form.slug} onChange={(e) => setField("slug", e.target.value)} className="editor-input font-mono text-sm" placeholder="es: obblighi-oll3-primo-soccorso" data-testid="editor-slug" />
                    </Field>
                    <Field label="Sommario" required>
                        <textarea required value={form.summary} onChange={(e) => setField("summary", e.target.value)} className="editor-input resize-none" rows={2} placeholder="Breve descrizione che compare nella lista e nelle anteprime" data-testid="editor-summary" />
                    </Field>

                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-[10px] uppercase tracking-[0.22em] font-mono text-ink-muted font-bold">Contenuto (Markdown) <span className="text-brand">*</span></span>
                            <span className="text-[10px] font-mono text-ink-muted">Supporta <code className="bg-ink/5 px-1">**grassetto**</code>, <code className="bg-ink/5 px-1"># Heading</code>, liste, tabelle</span>
                        </div>
                        <Tabs defaultValue="write" className="w-full">
                            <TabsList className="rounded-none">
                                <TabsTrigger value="write" className="rounded-none" data-testid="editor-tab-write">Scrivi</TabsTrigger>
                                <TabsTrigger value="preview" className="rounded-none" data-testid="editor-tab-preview"><Eye className="w-3.5 h-3.5 mr-1" /> Anteprima</TabsTrigger>
                            </TabsList>
                            <TabsContent value="write">
                                <textarea
                                    required
                                    value={form.content}
                                    onChange={(e) => setField("content", e.target.value)}
                                    className="w-full border border-ink/20 font-mono text-sm p-4 min-h-[480px] resize-y leading-relaxed focus:border-brand focus:outline-none"
                                    data-testid="editor-content"
                                />
                            </TabsContent>
                            <TabsContent value="preview">
                                <div className="border border-ink/10 p-6 bg-[#FAFAFA] min-h-[480px]" data-testid="editor-preview">
                                    <div className="prose-7m">
                                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{form.content || "_(vuoto)_"}</ReactMarkdown>
                                    </div>
                                </div>
                            </TabsContent>
                        </Tabs>
                    </div>
                </div>

                <aside className="lg:col-span-4 space-y-6">
                    <div className="bg-white border border-ink/10 p-6 space-y-5">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-ink-muted font-bold">Stato</div>
                            <div className="mt-2 flex items-center gap-2">
                                <span className={`px-3 py-1.5 text-xs font-mono uppercase ${form.status === "published" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`} data-testid="editor-status-badge">
                                    {form.status === "published" ? "Pubblicato" : "Bozza"}
                                </span>
                            </div>
                        </div>

                        <Field label="Categoria">
                            <Select value={form.category} onValueChange={(v) => setField("category", v)}>
                                <SelectTrigger className="rounded-none" data-testid="editor-category"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="Autore">
                            <input value={form.author} onChange={(e) => setField("author", e.target.value)} className="editor-input" data-testid="editor-author" />
                        </Field>
                        <Field label="Tag (separati da virgola)">
                            <input value={form.tags} onChange={(e) => setField("tags", e.target.value)} className="editor-input" placeholder="bls, oll3, azienda" data-testid="editor-tags" />
                        </Field>
                    </div>

                    <div className="bg-white border border-ink/10 p-6 space-y-4">
                        <Field label="Immagine di copertina (URL)">
                            <input value={form.cover_image} onChange={(e) => setField("cover_image", e.target.value)} className="editor-input font-mono text-sm" placeholder="https://..." data-testid="editor-cover" />
                        </Field>
                        {form.cover_image && (
                            <div className="aspect-[16/10] overflow-hidden bg-ink/5 border border-ink/10">
                                <img src={form.cover_image} alt="cover" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                            </div>
                        )}
                    </div>
                </aside>
            </div>

            <style>{`
                .editor-input {
                    width: 100%; background: transparent; border: 0;
                    border-bottom: 1px solid rgba(10,10,10,0.2);
                    padding: 10px 0; font-size: 15px; color: #0a0a0a;
                    outline: none; transition: border-color 0.2s;
                    font-family: "IBM Plex Sans", sans-serif;
                }
                .editor-input:focus { border-bottom-color: #FF3B30; }
                .editor-input::placeholder { color: rgba(10,10,10,0.35); }
                .prose-7m { font-size: 15px; line-height: 1.7; color: #1f2937; }
                .prose-7m h1 { font-family: "Cabinet Grotesk"; font-weight: 900; font-size: 32px; margin: 32px 0 16px; color: #0a0a0a; letter-spacing: -0.02em; }
                .prose-7m h2 { font-family: "Cabinet Grotesk"; font-weight: 800; font-size: 24px; margin: 28px 0 12px; color: #0a0a0a; }
                .prose-7m h3 { font-family: "Cabinet Grotesk"; font-weight: 700; font-size: 18px; margin: 20px 0 10px; color: #0a0a0a; }
                .prose-7m p { margin: 0 0 14px; }
                .prose-7m a { color: #FF3B30; text-decoration: underline; }
                .prose-7m strong { color: #0a0a0a; font-weight: 600; }
                .prose-7m ul { margin: 12px 0 18px 20px; list-style: square; }
                .prose-7m ol { margin: 12px 0 18px 20px; list-style: decimal; }
                .prose-7m li { margin-bottom: 4px; }
                .prose-7m blockquote { margin: 16px 0; padding: 12px 16px; border-left: 3px solid #FF3B30; background: #fff; font-style: italic; }
                .prose-7m code { background: #f4f4f5; padding: 2px 6px; font-size: 0.9em; font-family: "IBM Plex Mono"; }
                .prose-7m pre { background: #0a0a0a; color: #fafafa; padding: 16px; overflow-x: auto; margin: 16px 0; }
                .prose-7m table { width: 100%; border-collapse: collapse; margin: 16px 0; font-size: 14px; }
                .prose-7m th, .prose-7m td { border: 1px solid #e5e7eb; padding: 8px; text-align: left; }
                .prose-7m th { background: #fafafa; font-weight: 700; }
                .prose-7m hr { border: 0; border-top: 1px solid #e5e7eb; margin: 32px 0; }
            `}</style>
        </form>
    );
}

function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="text-[10px] uppercase tracking-[0.22em] font-mono text-ink-muted font-bold">
                {label}{required && <span className="text-brand"> *</span>}
            </span>
            <div className="mt-2">{children}</div>
        </label>
    );
}
